import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../Store/actions";
import { useIdForName } from "../../common/hooks";
import { strings } from "../../common/i18n";
import { format } from "../../common/lib";
import {
    Link,
    Button,
    Box,
    Chip,
    DialogContentText,
    Dialog,
    DialogTitle,
    DialogContent,
    Divider
} from "@mui/material/";
import FileUpload from "./FileUpload";
import ValidationErrorDetailPanel from "./ValidationErrorDetailPanel";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { Download, Error } from "@mui/icons-material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Loading from "../../common/components/Loading";
import styles from "./styles";
import { logToSentry } from "../../config/Sentry/";
import MatReactTable from "../../common/components/MaterialReactTable";
import { dateRangeFilterFn } from "../../common/components/MaterialReactTable/filterFns";

/** map upload data */
const mapUpload = (dataCheck: any) => {
    return {
        ...dataCheck,
        statusId: dataCheck.status.id,
        fileType: dataCheck.uploadPickup.fileType.description,
        statusName: dataCheck.status.name,
        statusDate: dataCheck.statusDate
            ? new Date(dataCheck.statusDate)
            : null, // Keep as Date object for filtering
        statusDateFormatted: `${format.dateTimeNew(dataCheck.statusDate)}`, // Formatted for display
        originalFilename: dataCheck.originalFilename.split("/").pop(),
        lastRowParsed: dataCheck.lastRowParsed,
        downloadCSV: dataCheck.blobPath
    };
};

/** sort date  */
const sortDate = (v1, v2) => new Date(v1) - new Date(v2);

/**
 * Uploads Table Component
 */

export default () => {
    const dispatch = useDispatch();

    const { dataCheck } = useSelector((state) => state);
    const { uploadStatus } = useSelector((state) => state.typesList);

    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    const onAddFile = () => setDialogOpen(true);
    const onDialogClose = () => setDialogOpen(false);

    const failedValidation = useIdForName(
        "failed_validation",
        uploadStatus.data
    );
    const completedWithOmission = useIdForName(
        "completed_with_omissions",
        uploadStatus.data
    );

    const data = useMemo(() => {
        if (dataCheck.data && dataCheck.data.length > 0)
            return dataCheck.data
                .map(mapUpload)
                .sort(
                    (a, b) => new Date(b.statusDate) - new Date(a.statusDate)
                );
        else return [];
    }, [dataCheck.data]);

    const columns = useMemo(() => {
        return [
            {
                id: "originalFilename",
                header: strings("fileName"),
                accessorKey: "originalFilename",
                filterVariant: "autocomplete",
                size: 200,
                Cell: ({ cell }) => {
                    const filename = cell.getValue();
                    return filename || "-";
                }
            },
            {
                id: "fileType",
                header: strings("fileType"),
                accessorKey: "fileType",
                filterVariant: "autocomplete",
                size: 150,
                Cell: ({ cell }) => {
                    const fileType = cell.getValue();
                    return fileType || "-";
                }
            },
            {
                id: "statusDate",
                header: strings("statusDate"),
                accessorKey: "statusDate", // Use the Date object for filtering
                filterVariant: "date-range",
                Cell: ({ cell }) => {
                    // Use the formatted date for display
                    return cell.row.original.statusDateFormatted || "-";
                },
                sortingFn: "datetime",
                filterFn: dateRangeFilterFn, // Use validated date filter
                size: 150
            },
            {
                id: "lastRowParsed",
                header: strings("rowsProcessed"),
                accessorKey: "lastRowParsed",
                filterVariant: "text", // Use text filter for numbers
                filterFn: "equals", // or "between" for range filtering
                Cell: ({ cell }) => {
                    const rows = cell.getValue();
                    return rows != null ? rows.toString() : "-";
                },
                size: 150
            },
            {
                id: "statusName",
                header: strings("status"),
                accessorKey: "statusName",
                filterVariant: "multi-select",
                Cell: ({ cell, row }) => {
                    const status = cell.getValue();
                    const { statusId } = row.original;
                    if (!status) return "-";
                    const chipSx = { px: 0.5, "& .MuiChip-icon": { ml: 0.5 } };
                    if (statusId === failedValidation)
                        return (
                            <Chip
                                icon={<Error fontSize="small" />}
                                label={status}
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={chipSx}
                            />
                        );
                    if (statusId === completedWithOmission)
                        return (
                            <Chip
                                icon={<WarningAmberIcon fontSize="small" />}
                                label={status}
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={chipSx}
                            />
                        );
                    return (
                        <Chip
                            icon={<CheckCircleOutlineIcon fontSize="small" />}
                            label={status}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={chipSx}
                        />
                    );
                },
                size: 200
            }
        ];
    }, [failedValidation, completedWithOmission]);

    const rowHasErrors = (statusId) =>
        statusId === failedValidation || statusId === completedWithOmission;

    const rowActions = (row) => {
        const { blobPath } = row.original;
        if (!blobPath) return null;
        return (
            <Link href={blobPath}>
                <Button
                    size="small"
                    title={strings("download")}
                    color="primary"
                    startIcon={<Download color="success" fontSize="small" />}
                />
            </Link>
        );
    };

    const renderDetailPanel = ({ row }) => (
        <ValidationErrorDetailPanel uploadId={row.original.id} />
    );

    const muiExpandButtonProps = ({ row }) => {
        const hasErrors = rowHasErrors(row.original.statusId);
        return {
            sx: { visibility: hasErrors ? "visible" : "hidden" }
        };
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                await dispatch(actions.dataCheck.create.getRequest());
            } catch (error) {
                const errStr = error.toString();
                console.error(errStr);
                logToSentry("Data-check: On row click:", { error });
                dispatch(
                    actions.toast.create.error(
                        strings("errors.onDataCheckLoadFailed")
                    )
                );
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const toolBarComponents = () => (
        <Button
            sx={styles.primaryButton}
            variant="contained"
            size="small"
            color="primary"
            onClick={onAddFile}
            endIcon={<FileUploadIcon />}
        >
            {strings("importData")}
        </Button>
    );

    return (
        <>
            {loading && <Loading loadingText={strings("loadingDataCheck")} />}
            <MatReactTable
                persistKey="dataCheckUploads"
                columns={columns}
                data={data}
                loading={loading}
                rowActions={rowActions}
                toolBarComponents={toolBarComponents}
                noRowsMessage={strings("messages.noFileUploads")}
                initialState={{
                    sorting: [{ id: "statusDate", desc: true }]
                }}
                loadingText="loadingDataCheck"
                renderDetailPanel={renderDetailPanel}
                muiExpandButtonProps={muiExpandButtonProps}
                muiExpandAllButtonProps={{ sx: { display: "none" } }}
            />
            <Dialog onClose={onDialogClose} open={dialogOpen}>
                <DialogTitle>{strings("selectFileType")}</DialogTitle>
                <Divider />
                <DialogContent>
                    <DialogContentText>
                        {strings("messages.selectFileTypeDescription")}
                    </DialogContentText>
                    <FileUpload onClose={onDialogClose} />
                </DialogContent>
            </Dialog>
        </>
    );
};
